/**
 * Parent read-only preview of linked student faculty feedback.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';

const LABELS = {
  TEACHING_QUALITY: 'Teaching quality',
  SUBJECT_KNOWLEDGE: 'Subject knowledge',
  COMMUNICATION: 'Communication',
  PUNCTUALITY: 'Punctuality',
  DOUBT_RESOLUTION: 'Doubt resolution',
  COURSE_COVERAGE: 'Course coverage',
  ASSESSMENT_FAIRNESS: 'Assessment fairness',
};

export default function ParentStudentFeedbackPreview() {
  const { submissionId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/parent-feedback/student-submissions/${submissionId}`)
      .then((res) => setData(res.data?.data || null))
      .catch((err) => setError(err.response?.data?.error?.message || 'Could not load this submission.'))
      .finally(() => setLoading(false));
  }, [submissionId]);

  return (
    <Layout>
      <div className="par-form-page">
        <div className="genz-inner">
          <p className="text-xs uppercase tracking-[0.2em] text-rose-200/80 mb-2">Student feedback · Locked preview</p>
          <h1 className="text-3xl font-semibold text-white m-0">
            {data ? `${data.subject_name} · ${data.subject_code}` : 'Student feedback'}
          </h1>
          <p className="genz-lock mt-3">Read-only. You can observe your ward’s submitted feedback; it cannot be edited here.</p>
          {error ? <div className="alert-error mt-4">{error}</div> : null}
          {loading ? <div className="genz-card mt-6">Loading preview…</div> : null}
          {data ? (
            <div className="genz-card mt-6 space-y-4">
              <p className="text-slate-300 m-0">
                {[data.faculty_name, data.cycle_name, data.academic_year, data.submitted_at ? new Date(data.submitted_at).toLocaleString() : null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {(data.ratings || []).map((row) => (
                <div key={row.criterion} className="genz-criterion">
                  <div className="flex justify-between gap-3">
                    <p className="text-white font-medium m-0">{LABELS[row.criterion] || row.criterion}</p>
                    <span>{row.rating}/5</span>
                  </div>
                  {row.comments ? <p className="text-sm text-slate-300 m-0">{row.comments}</p> : null}
                </div>
              ))}
              <p className="text-slate-200 m-0">Overall {data.overall_rating ?? '—'}</p>
              {data.strengths ? <p className="text-slate-200 m-0"><span className="text-rose-200">Strengths: </span>{data.strengths}</p> : null}
              {data.improvements ? <p className="text-slate-200 m-0"><span className="text-sky-200">Improvements: </span>{data.improvements}</p> : null}
              {data.additional_comments ? <p className="text-slate-200 m-0"><span className="text-slate-400">Notes: </span>{data.additional_comments}</p> : null}
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
